import { useState } from "react";
import { ArrowUpDown, Check, ChevronDown } from "lucide-react";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/ui/popover";
import { cn } from "~/lib/utils";
import { FilterLabel } from "./FilterLabel";
import { FILTER_CONTROL_MUTED_ICON_CLASS } from "./filterControlStyles";
import { FilterControlButton } from "./FilterControl";

// // // //

interface SortOption {
    value: string;
    label: string;
}

export enum SortOptions {
    dataRoom = "data-room",
    yieldDesc = "yield-desc",
    yieldAsc = "yield-asc",
    return1yDesc = "return-1y-desc",
    return1mDesc = "return-1m-desc",
    aumDesc = "aum-desc",
    aumAsc = "aum-asc",
}

const SORT_OPTIONS: SortOption[] = [
    { value: SortOptions.dataRoom, label: "Data Room Available" },
    { value: SortOptions.yieldDesc, label: "Highest 30D APY" },
    { value: SortOptions.yieldAsc, label: "Lowest 30D APY" },
    { value: SortOptions.return1yDesc, label: "Highest 1Y Return" },
    { value: SortOptions.return1mDesc, label: "Highest 1M Return" },
    { value: SortOptions.aumDesc, label: "Highest AUM" },
    { value: SortOptions.aumAsc, label: "Lowest AUM" },
];

interface Props {
    value: string;
    onChange: (value: string) => void;
    showLabel?: boolean;
}

/**
 * Sort input control for the asset screener listing.
 */
export function AssetScreenerSortInput(props: Props) {
    const { value, onChange, showLabel = true } = props;
    const [open, setOpen] = useState(false);

    const selectedOption = SORT_OPTIONS.find((opt) => opt.value === value);

    const handleSelect = (optionValue: string) => {
        onChange(optionValue);
        setOpen(false);
    };

    return (
        <div className={cn({ "space-y-1": showLabel })}>
            {showLabel && <FilterLabel label="Sort By" />}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <FilterControlButton
                        role="combobox"
                        aria-expanded={open}
                        aria-label="Select sort order"
                    >
                        <div className="flex items-center">
                            <ArrowUpDown
                                className={`${FILTER_CONTROL_MUTED_ICON_CLASS} mr-1.5 h-3.5 w-3.5`}
                            />
                            <span className="font-normal">
                                {selectedOption?.label ?? "Sort by..."}
                            </span>
                        </div>
                        <ChevronDown
                            className={`${FILTER_CONTROL_MUTED_ICON_CLASS} h-3.5 w-3.5`}
                        />
                    </FilterControlButton>
                </PopoverTrigger>
                <PopoverContent
                    className="w-52 p-1 rounded-lg shadow-xs"
                    align="start"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                >
                    <div className="flex flex-col">
                        {SORT_OPTIONS.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => handleSelect(option.value)}
                                className={cn(
                                    "flex items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors",
                                    "hover:bg-muted focus:bg-muted focus:outline-none",
                                    value === option.value && "bg-tertiary",
                                )}
                                tabIndex={0}
                                aria-selected={value === option.value}
                            >
                                <span>{option.label}</span>
                                {value === option.value && (
                                    <Check className="h-3.5 w-3.5 text-secondary-foreground" />
                                )}
                            </button>
                        ))}
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
}
